import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as BellIcon } from '@/assets/icons/bell-icon.svg';
import Button from '@/components/Button';
import Text from '@/components/Text';
import useDocumentSignedNotifications from '@/hooks/payments/useDocumentSignedNotifications';
import useVacationRequestedNotifications from '@/hooks/payments/useVacationRequestedNotifications';
import useAlerts from '@/hooks/useAlerts';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { api } from '@/utils/api';
import { RootState } from '@/utils/store';
import { scrollBarStyles } from '@/utils/theme';

import Notification from './Notification';

const Wrap = styled.div`
  position: relative;
`;

const StyledButton = styled(Button)`
  width: 48px;
  position: relative;
  svg {
    margin: 0;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 348px;
  z-index: 4;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  border: solid 1px ${({ theme }) => theme.colors.lightGray};
  box-shadow: 0px 0px 30px 0px #25282b0f;
`;

const DropdownHeader = styled.div`
  border-bottom: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  height: 46px;
  align-items: center;
  justify-content: space-between;
  padding: 2px 16px;
`;

const DropdownFooter = styled.div`
  padding: 16px;
  padding-top: 12px;
  text-align: center;
  display: flex;
  justify-content: space-between;
`;

const CloseButton = styled.button`
  display: flex;
  height: 20px;
  width: 20px;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray};
  transition: all ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const NotifsCount = styled.div`
  display: inline-flex;
  width: 26px;
  height: 18px;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.success};
  border-radius: 9px;
`;

const HeadingHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Placeholder = styled.div`
  padding: 40px 12px;
  text-align: center;
`;

const NotifsDot = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  border: solid 1px ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.success};
  border-radius: 50%;
  top: 6px;
  right: 14px;
`;

const NotifsHolder = styled.div`
  max-height: 400px;
  overflow-y: auto;
  ${scrollBarStyles};
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.black};
  text-align: left;
`;

const TabsHolder = styled.div`
  padding: 12px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

const TabsContainer = styled.div`
  display: flex;
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.lightGray};
  padding: 3px;
`;

const NotifCount = styled.span`
  display: flex;
  height: 15px;
  width: 26px;
  align-items: center;
  justify-content: center;
  transition: all ease-out 0.2s;
  color: ${({ theme }) => theme.colors.black};
  background-color: ${({ theme }) => theme.colors.lightGray};
  font-size: 9px;
  border-radius: 9px;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  gap: 8px;
  flex: 1;
  height: 23px;
  padding: 0 4px;
  border: none;
  background: none;
  cursor: pointer;
  transition: all ease-out 0.2s;
  color: ${({ theme }) => theme.colors.gray};
  font-weight: 700;
  border-radius: 4px;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
  ${({ $active, theme }) =>
    $active &&
    css`
      background-color: ${theme.colors.purple};
      color: ${theme.colors.white};
      &:hover {
        color: ${({ theme }) => theme.colors.white};
      }
      ${NotifCount} {
        background-color: ${theme.colors.white};
        color: ${theme.colors.purple};
      }
    `}
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
  `}
`;

const NoNotifs = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const MarkAsReadButton = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
  }
`;

export enum NotifType {
  Documents = 'documents',
  Vacation = 'vacation',
}

const FreelancersNotifications = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState<NotifType>(NotifType.Documents);

  const componentRef = useRef(document.createElement('div'));

  useOnClickOutside(componentRef, () => setShowDropdown(false));

  const user = useSelector((rootState: RootState) => rootState.auth.user);
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const { notifications: docSignedNotifications } =
    useDocumentSignedNotifications(user?.id);
  const { notifications: vacationNotifications } =
    useVacationRequestedNotifications(user?.id);

  const activeNotifications =
    activeTab === NotifType.Documents
      ? docSignedNotifications
      : vacationNotifications;

  const unreadActiveNotifications = activeNotifications?.filter(
    (n) => n.readAt === null,
  );

  const totalNotificationsCount =
    (docSignedNotifications?.length || 0) +
    (vacationNotifications?.length || 0);

  const handleMarkAllAsRead = async () => {
    if (!unreadActiveNotifications?.length) return;

    try {
      const notificationType =
        activeTab === NotifType.Documents
          ? 'App\\Notifications\\DocumentSigned'
          : 'App\\Notifications\\FreelancerHolidayRequested';

      await api.post('/v1/notifications/mark-read', {
        types: [notificationType],
      });

      const queryKey =
        activeTab === NotifType.Documents
          ? 'document_signed'
          : 'vacation_requested';

      await queryClient.invalidateQueries([queryKey], {
        exact: false,
      });
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <Wrap ref={componentRef}>
      <StyledButton
        color="secondary"
        small
        onClick={() => setShowDropdown((prev) => !prev)}
      >
        <BellIcon />
        {totalNotificationsCount > 0 && <NotifsDot />}
      </StyledButton>
      {showDropdown && (
        <Dropdown>
          <DropdownHeader>
            <HeadingHolder>
              <Text $type="caption">Notifications</Text>
              {totalNotificationsCount > 0 && (
                <NotifsCount>
                  <Text $color="white" $type="extraSmall">
                    {totalNotificationsCount}
                  </Text>
                </NotifsCount>
              )}
            </HeadingHolder>
            <CloseButton onClick={() => setShowDropdown(false)}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 5.99994L6 13.9999"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 13.9999L6 5.99994"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CloseButton>
          </DropdownHeader>
          <TabsHolder>
            <TabsContainer>
              <Tab
                $active={activeTab === NotifType.Documents}
                onClick={() => setActiveTab(NotifType.Documents)}
              >
                DocuSign
                <NotifCount>{docSignedNotifications?.length ?? 0}</NotifCount>
              </Tab>
              <Tab
                $active={activeTab === NotifType.Vacation}
                onClick={() => setActiveTab(NotifType.Vacation)}
              >
                Vacation Requests
                <NotifCount>{vacationNotifications?.length ?? 0}</NotifCount>
              </Tab>
            </TabsContainer>
          </TabsHolder>
          {activeNotifications?.length ? (
            <NotifsHolder key={activeTab}>
              {activeNotifications.map((notification: any) => (
                <Notification
                  key={notification.id}
                  notification={notification}
                  type={activeTab}
                />
              ))}
            </NotifsHolder>
          ) : (
            <Placeholder key={activeTab}>
              <NoNotifs $color="gray" $type="smallHeadline">
                <BellIcon /> No unread notifications
              </NoNotifs>
            </Placeholder>
          )}
          <DropdownFooter>
            <StyledLink to="/general/freelancers-notification-center">
              <Text $type="smallHeadline" $color="black">
                Go to notifications center
              </Text>
            </StyledLink>
            <MarkAsReadButton
              as="button"
              $type="smallHeadline"
              $color="gray"
              onClick={handleMarkAllAsRead}
              disabled={!unreadActiveNotifications?.length}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M13.5 4.50049L6.5 11.5002L3 8.00049"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Mark as Read
            </MarkAsReadButton>
          </DropdownFooter>
        </Dropdown>
      )}
    </Wrap>
  );
};

export default FreelancersNotifications;
